import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { ACTIVIDADES_SAT } from '../catalogos/catalogoSat';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtFecha(val) {
    if (!val) return '—';
    const digits = String(val).replace(/\D/g, '');
    if (digits.length === 8) {
        return `${digits.slice(6, 8)}/${digits.slice(4, 6)}/${digits.slice(0, 4)}`;
    }
    const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return String(val);
}

function nombrePais(paises, clave) {
    if (!clave) return '—';
    const p = (paises || []).find((x) => x.clave === clave);
    return p ? p.nombre : String(clave);
}

function nombreActividad(codigo) {
    if (!codigo) return '—';
    const a = ACTIVIDADES_SAT.find((x) => x.codigo === codigo);
    return a ? a.nombre : String(codigo);
}

function nombreIdTipo(tipo) {
    const map = {
        INE: 'INE / IFE',
        PASAPORTE: 'Pasaporte',
        CEDULA: 'Cédula profesional',
        INM: 'Documento migratorio',
    };
    return map[tipo] || tipo || '—';
}

function fmtConoceBeneficiario(v) {
    if (v === 'si') return 'Sí';
    if (v === 'no') return 'No';
    return '—';
}

function val(x) {
    const s = String(x || '').trim();
    return s || '—';
}

// ── Paleta ───────────────────────────────────────────────────────────────────
//const ROJO_OSCURO = '#a80017';
const ROJO_SEC = '#e41b23';
const GRIS = '#555555';
const GRIS_CLARO = '#f8f9fa';
const BORDE = '#cccccc';
const BG_FICHA = '#f0f4f8';
const BG_REP = '#f8fafc';

// ── Estilos ──────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
    pagina: {
        paddingTop: 28,
        paddingBottom: 28,
        paddingLeft: 30,
        paddingRight: 30,
        fontFamily: 'Helvetica',
        fontSize: 9,
        color: '#111111',
    },

    // Cabecera principal
    header: {
        backgroundColor: GRIS_CLARO,
        paddingTop: 11,
        paddingBottom: 11,
        paddingLeft: 16,
        paddingRight: 16,
        marginBottom: 10,
    },
    headerFila: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    headerTitulo: {
        color: '#000000',
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        letterSpacing: 0.4,
    },
    headerSub: {
        color: '#000000',
        fontSize: 7.5,
        marginTop: 4,
    },
    badgePersona: {
        backgroundColor: GRIS_CLARO,
        color: '#000000',
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 9,
        paddingRight: 9,
        borderRadius: 3,
    },

    // Ficha de operación
    opCard: {
        flexDirection: 'row',
        backgroundColor: BG_FICHA,
        borderWidth: 0.5,
        borderColor: BORDE,
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 10,
        paddingRight: 10,
        marginBottom: 10,
    },
    opCampo: {
        flex: 1,
        paddingRight: 10,
    },
    opEtiqueta: {
        fontSize: 7,
        color: GRIS,
        marginBottom: 2,
    },
    opValor: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
    },

    // Encabezado de sección
    secHeader: {
        backgroundColor: GRIS_CLARO,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
        paddingRight: 8,
        marginTop: 10,
        marginBottom: 5,
    },
    secTitulo: {
        color: '#000000',
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
    },

    // Fila y campo
    fila: {
        flexDirection: 'row',
        marginBottom: 5,
    },
    campo: {
        flex: 1,
        paddingRight: 8,
    },
    etiqueta: {
        fontSize: 6.8,
        color: GRIS,
        marginBottom: 2,
    },
    valor: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        borderBottomWidth: 0.5,
        borderBottomColor: BORDE,
        paddingBottom: 2,
        minHeight: 12,
    },

    // Caja representante legal
    repBox: {
        borderWidth: 0.8,
        borderColor: ROJO_SEC,
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 9,
        paddingRight: 9,
        marginTop: 4,
        backgroundColor: BG_REP,
    },
    repTitulo: {
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        color: ROJO_SEC,
        marginBottom: 6,
    },

    // Definición legal Beneficiario Controlador
    articuloBox: {
        marginTop: 12,
        marginBottom: 8,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 8,
        paddingRight: 8,
        borderWidth: 0.5,
        borderColor: BORDE,
        backgroundColor: GRIS_CLARO,
    },
    articuloTitulo: {
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 5,
        color: '#111111',
    },
    articuloTexto: {
        fontSize: 6.5,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
        marginBottom: 4,
    },
    articuloInciso: {
        fontSize: 6.5,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
        marginBottom: 3,
        paddingLeft: 8,
    },
    articuloSubinciso: {
        fontSize: 6.5,
        color: '#333333',
        lineHeight: 1.45,
        textAlign: 'justify',
        marginBottom: 2,
        paddingLeft: 16,
    },

    // Pie de página / firmas
    footer: {
        marginTop: 16,
        paddingTop: 8,
        borderTopWidth: 0.5,
        borderTopColor: BORDE,
    },
    firmaFila: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 24,
    },
    firmaBloque: {
        width: '44%',
        borderTopWidth: 0.8,
        borderTopColor: '#333333',
        paddingTop: 4,
    },
    firmaLabel: {
        fontSize: 7,
        color: GRIS,
        textAlign: 'center',
    },
    nota: {
        fontSize: 6.5,
        color: '#999999',
        textAlign: 'center',
        marginTop: 10,
    },
});

// ── Componentes de apoyo ─────────────────────────────────────────────────────

function Seccion({ letra, titulo }) {
    return (
        <View style={S.secHeader}>
            <Text style={S.secTitulo}>
                {letra}.  {titulo}
            </Text>
        </View>
    );
}

function Fila({ children }) {
    return <View style={S.fila}>{children}</View>;
}

function Campo({ label, value, flex = 1 }) {
    return (
        <View style={[S.campo, { flex }]}>
            <Text style={S.etiqueta}>{label}</Text>
            <Text style={S.valor}>{val(value)}</Text>
        </View>
    );
}

// ── Componente principal ─────────────────────────────────────────────────────

/**
 * Formato de identificación PLD en PDF.
 * @param {object}   props.operacion       Objeto de la operación (denominacion, tipo_operacion)
 * @param {object[]} props.sujetos         Lista de sujetos en la operación
 * @param {object}   props.sujetoDetalle   Datos completos del sujeto (campos pld_*)
 * @param {string}   [props.rolEnOperacion]
 * @param {Array}    [props.paises]        Catálogo de países [{ clave, nombre }]
 */
export default function HojaFrontal({ operacion, sujetos, sujetoDetalle: d, rolEnOperacion, paises }) {
    const esMoral = d && (d.tipo_persona === 'Moral' || d.tipo_persona === 'Fideicomiso');
    const rep = (d && d.representante_legal) || {};

    const nombreCompleto = d
        ? [d.nombre, d.paterno, d.materno].filter(Boolean).join(' ')
        : '—';

    const nombreRep = [rep.nombre, rep.paterno, rep.materno].filter(Boolean).join(' ');

    const denom = (operacion && operacion.denominacion) || '—';
    const tipoOp = (operacion && operacion.tipo_operacion) || '—';

    const calleNum = [
        d && d.pld_calle,
        d && d.pld_num_ext ? `No. ${d.pld_num_ext}` : null,
        d && d.pld_num_int ? `Int. ${d.pld_num_int}` : null,
    ]
        .filter(Boolean)
        .join(', ');

    const calleNumRep = [
        rep.pld_calle,
        rep.pld_num_ext ? `No. ${rep.pld_num_ext}` : null,
        rep.pld_num_int ? `Int. ${rep.pld_num_int}` : null,
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <Document>
            <Page size="A4" style={S.pagina}>

                {/* ── ENCABEZADO ───────────────────────────────────────── */}
                <View style={S.header}>
                    <View style={S.headerFila}>
                        <View>
                            <Text style={S.headerTitulo}>
                                FORMATO DE IDENTIFICACIÓN — PLD
                            </Text>
                            <Text style={S.headerSub}>
                                Prevención de Lavado de Dinero  ·  Anexo de identificación de sujeto
                            </Text>
                        </View>
                        <Text style={S.badgePersona}>
                            {esMoral ? 'PERSONA MORAL' : 'PERSONA FÍSICA'}
                        </Text>
                    </View>
                </View>

                {/* ── FICHA DE OPERACIÓN ───────────────────────────────── */}
                <View style={S.opCard}>
                    <View style={S.opCampo}>
                        <Text style={S.opEtiqueta}>Denominación del expediente</Text>
                        <Text style={S.opValor}>{denom}</Text>
                    </View>
                    <View style={S.opCampo}>
                        <Text style={S.opEtiqueta}>Tipo de operación</Text>
                        <Text style={S.opValor}>{tipoOp}</Text>
                    </View>
                    <View style={S.opCampo}>
                        <Text style={S.opEtiqueta}>Rol en la operación</Text>
                        <Text style={S.opValor}>{val(rolEnOperacion)}</Text>
                    </View>
                    <View style={S.opCampo}>
                        <Text style={S.opEtiqueta}>Sujetos en la operación</Text>
                        <Text style={S.opValor}>
                            {String(Array.isArray(sujetos) ? sujetos.length : 0)}
                        </Text>
                    </View>
                </View>

                {/* ── A. IDENTIDAD Y ORIGEN ────────────────────────────── */}
                <Seccion letra="A" titulo="IDENTIDAD Y ORIGEN" />
                <Fila>
                    <Campo
                        label={esMoral ? 'Razón social / Denominación' : 'Nombre completo'}
                        value={nombreCompleto}
                        flex={3}
                    />
                    <Campo
                        label={esMoral ? 'Fecha de constitución' : 'Fecha de nacimiento'}
                        value={fmtFecha(d && d.pld_fecha_nac_const)}
                        flex={1}
                    />
                </Fila>
                <Fila>
                    <Campo
                        label="País de nacionalidad"
                        value={nombrePais(paises, d && d.pld_pais_nacionalidad)}
                    />
                    {!esMoral ? (
                        <Campo
                            label="País de nacimiento"
                            value={nombrePais(paises, d && d.pld_pais_nacimiento)}
                        />
                    ) : null}
                    <Campo
                        label="Actividad / Giro (SAT)"
                        value={
                            (d && d.pld_actividad_giro_descripcion) ||
                            nombreActividad(d && d.pld_actividad_giro_codigo)
                        }
                        flex={2}
                    />
                </Fila>
                <Fila>
                    {!esMoral ? (
                        <Campo
                            label="Lugar de nacimiento"
                            value={d && d.pld_lugar_nacimiento}
                        />
                    ) : null}
                    {!esMoral ? (
                        <Campo
                            label="Lugar de residencia"
                            value={d && d.pld_lugar_residencia}
                        />
                    ) : null}
                    {!esMoral ? (
                        <Campo
                            label="Condición de estancia"
                            value={d && d.pld_condicion_estancia}
                        />
                    ) : null}
                </Fila>

                {/* ── B. IDENTIFICADORES Y CONTACTO ────────────────────── */}
                <Seccion letra="B" titulo="IDENTIFICADORES Y CONTACTO" />
                <Fila>
                    <Campo label="RFC" value={d && d.rfc} />
                    {!esMoral ? (
                        <Campo label="CURP" value={d && d.curp} />
                    ) : null}
                    <Campo label="Teléfono" value={d && d.pld_telefono} />
                    <Campo
                        label="Correo electrónico"
                        value={d && d.pld_correo}
                        flex={2}
                    />
                </Fila>

                {/* ── C. DOMICILIO ─────────────────────────────────────── */}
                <Seccion letra="C" titulo="DOMICILIO" />
                <Fila>
                    <Campo
                        label="Calle y número"
                        value={calleNum}
                        flex={2}
                    />
                    <Campo label="Colonia" value={d && d.pld_colonia} />
                    <Campo label="C.P." value={d && d.pld_cp} flex={0.6} />
                </Fila>
                <Fila>
                    <Campo label="Ciudad / Municipio" value={d && d.pld_ciudad} />
                    <Campo label="Estado" value={d && d.pld_estado} />
                    <Campo
                        label="País"
                        value={nombrePais(paises, d && d.pld_pais_domicilio)}
                    />
                </Fila>

                {/* ── D. IDENTIFICACIÓN OFICIAL (solo Persona Física) ──── */}
                {!esMoral ? (
                    <>
                        <Seccion letra="D" titulo="IDENTIFICACIÓN OFICIAL" />
                        <Fila>
                            <Campo
                                label="Tipo de identificación"
                                value={nombreIdTipo(d && d.pld_id_tipo)}
                            />
                            <Campo
                                label="Autoridad emisora"
                                value={d && d.pld_id_autoridad}
                            />
                            <Campo
                                label="Número de identificación"
                                value={d && d.pld_id_numero}
                            />
                        </Fila>

                        <Seccion letra="E" titulo="BENEFICIARIO CONTROLADOR" />
                        <Fila>
                            <Campo
                                label="¿Conoce usted a algún beneficiario controlador?"
                                value={fmtConoceBeneficiario(
                                    d && d.pld_conoce_beneficiario_controlador
                                )}
                                flex={
                                    d && d.pld_conoce_beneficiario_controlador === 'si' ? 1 : 2
                                }
                            />
                            {d && d.pld_conoce_beneficiario_controlador === 'si' ? (
                                <Campo
                                    label="¿Quién? (nombre completo)"
                                    value={d.pld_beneficiario_controlador_nombre}
                                    flex={2}
                                />
                            ) : null}
                        </Fila>
                    </>
                ) : null}

                {/* ── D. REPRESENTANTE LEGAL (solo Persona Moral) ──────── */}
                {esMoral ? (
                    <>
                        <Seccion letra="D" titulo="REPRESENTANTE LEGAL" />
                        <View style={S.repBox}>
                            <Text style={S.repTitulo}>
                                Datos del representante legal / apoderado
                            </Text>
                            <Fila>
                                <Campo
                                    label="Nombre completo del representante"
                                    value={nombreRep}
                                    flex={3}
                                />
                                <Campo
                                    label="Fecha de nacimiento"
                                    value={fmtFecha(rep.pld_fecha_nac_const)}
                                    flex={1}
                                />
                            </Fila>
                            <Fila>
                                <Campo label="RFC" value={rep.rfc} />
                                <Campo label="CURP" value={rep.curp} />
                            </Fila>
                            <Fila>
                                <Campo
                                    label="País de nacimiento"
                                    value={nombrePais(paises, rep.pld_pais_nacimiento)}
                                />
                                <Campo
                                    label="Lugar de nacimiento"
                                    value={rep.pld_lugar_nacimiento}
                                    flex={2}
                                />
                            </Fila>
                            <Fila>
                                <Campo
                                    label="Lugar de residencia"
                                    value={rep.pld_lugar_residencia}
                                    flex={2}
                                />
                                <Campo
                                    label="Nacionalidad"
                                    value={rep.pld_nacionalidad}
                                />
                            </Fila>
                            <Fila>
                                <Campo
                                    label="Condición de estancia"
                                    value={rep.pld_condicion_estancia}
                                />
                                <Campo
                                    label="Ocupación"
                                    value={rep.pld_ocupacion}
                                    flex={2}
                                />
                            </Fila>
                            <Fila>
                                <Campo label="Teléfono" value={rep.pld_telefono} />
                                <Campo
                                    label="Correo electrónico"
                                    value={rep.pld_correo}
                                    flex={2}
                                />
                            </Fila>
                            <Fila>
                                <Campo
                                    label="Tipo de identificación"
                                    value={nombreIdTipo(rep.pld_id_tipo)}
                                />
                                <Campo
                                    label="Autoridad emisora"
                                    value={rep.pld_id_autoridad}
                                />
                                <Campo
                                    label="Número de identificación"
                                    value={rep.pld_id_numero}
                                />
                            </Fila>
                            <Text style={[S.repTitulo, { marginTop: 6 }]}>
                                Domicilio del representante legal
                            </Text>
                            <Fila>
                                <Campo
                                    label="Calle y número"
                                    value={calleNumRep}
                                    flex={2}
                                />
                                <Campo label="Colonia" value={rep.pld_colonia} />
                                <Campo label="C.P." value={rep.pld_cp} flex={0.6} />
                            </Fila>
                            <Fila>
                                <Campo
                                    label="Ciudad, Alcaldía o Municipio"
                                    value={rep.pld_ciudad}
                                />
                                <Campo label="Estado" value={rep.pld_estado} />
                                <Campo
                                    label="País"
                                    value={nombrePais(paises, rep.pld_pais_domicilio)}
                                />
                            </Fila>
                        </View>
                    </>
                ) : null}

                {/* ── ARTÍCULO 3 / BENEFICIARIO CONTROLADOR ─────────────── */}
                <View style={S.articuloBox}>
                    <Text style={S.articuloTitulo}>
                        Artículo 3. Para los efectos de esta Ley, se entenderá por:
                    </Text>
                    <Text style={S.articuloTexto}>
                        III. Beneficiario Controlador, a la persona física o grupo de personas
                        físicas que:
                    </Text>
                    <Text style={S.articuloInciso}>
                        a).- Directamente o por medio de alguna persona Cliente o Usuaria
                        obtiene, en última instancia, el beneficio de goce, uso, disfrute,
                        aprovechamiento o disposición del bien o servicio derivado de la
                        realización de un acto u operación con quien realice una Actividad
                        Vulnerable, o
                    </Text>
                    <Text style={S.articuloInciso}>
                        b).- Ejerce el control efectivo en última instancia de aquella persona
                        moral que, en su carácter de Cliente o Usuaria, lleve a cabo actos u
                        operaciones con quien realice una Actividad Vulnerable, así como las
                        personas por cuenta de quienes celebra alguno de ellos.
                    </Text>
                    <Text style={S.articuloTexto}>
                        Se entiende que una persona o grupo de personas controla de manera
                        efectiva en última instancia a una persona moral cuando, a través de la
                        titularidad de valores, por contrato o cualquier otro acto, en términos
                        de las Reglas de Carácter General aplicables, puede:
                    </Text>
                    <Text style={S.articuloSubinciso}>
                        i) Imponer, directa o indirectamente, decisiones en las asambleas
                        generales de accionistas, socios u órganos equivalentes, o nombrar o
                        destituir a la mayoría de los consejeros, administradores o sus
                        equivalentes;
                    </Text>
                    <Text style={S.articuloSubinciso}>
                        ii) Mantener la titularidad de los derechos que permitan, directa o
                        indirectamente, ejercer el voto respecto de más del veinticinco por
                        ciento del capital social, o
                    </Text>
                    <Text style={S.articuloSubinciso}>
                        iii) Dirigir, directa o indirectamente, la administración, la estrategia
                        o las principales políticas de la misma.
                    </Text>
                    <Text style={S.articuloTexto}>
                        Para efectos del Capítulo IV Bis de esta Ley, se entenderá como
                        Beneficiario Controlador a quien tenga el control de una persona moral
                        en términos del inciso b) anterior, aunque dicha persona moral no sea
                        Cliente o Usuaria de alguien que realice Actividades Vulnerables o se
                        lleven a cabo actos u operaciones con éstas a su nombre.
                    </Text>
                    <Text style={[S.articuloTexto, { marginBottom: 0 }]}>
                        Para efectos de esta Ley y demás disposiciones jurídicas aplicables, la
                        definición de Beneficiario Controlador, es equiparable a beneficiario
                        final y propietario real.
                    </Text>
                </View>

                {/* ── PIE DE PÁGINA / FIRMAS ───────────────────────────── */}
                <View style={S.footer}>
                    <View style={S.firmaFila}>
                        <View style={S.firmaBloque}>
                            <Text style={S.firmaLabel}>
                                Firma del cliente
                            </Text>
                        </View>
                    </View>
                </View>

            </Page>
        </Document>
    );
}
